import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {Subject} from 'rxjs';
import {map, switchMap, take, takeUntil} from 'rxjs/operators';
import {Sort} from '@angular/material/sort';
import {MatDialog} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {addFieldMapFiltersForPagedRequestDTO, getSortDirection} from '../../../../../common/util/Util';
import {DateUtil} from '../../../../../common/util/DateUtil';
import {TableControl} from '../../../../../common/models/TableControl';
import {ProductService} from '../../../../services/product.service';
import {EntRequestBuilder} from '../../../../services/ent-request-builder';
import {ProductDTO} from '../../../../models/ProductDTO';
import {PagedResponseDTO} from '../../../../../common/models/PagedResponseDTO';
import {ConfirmationDialogComponent} from '../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {detailsExpand} from '../../../../../common';
import {SharedService} from '../../../../../common/services/shared-service';
import {PagedRequestDTO} from '../../../../../common/models/PagedRequestDTO';

enum TableColumns {
  Icons = 'icons',
  ProductId = 'productId',
  ProductCode = 'productCode',
  Name = 'name',
  ProcessingClass = 'processingClass',
  ProviderNetwork = 'providerNetwork',
  Payer = 'payer',
  StartDate = 'startDate',
  EndDate = 'endDate'
}

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: [
    '../../../../../../../styles/global/_management-styles.scss'
  ],
  animations: [detailsExpand]
})
export class ProductsComponent implements OnInit, OnDestroy {
  readonly destroyed$ = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly TableColumns = TableColumns;

  productsDataSource: MatTableDataSource<ProductDTO> = new MatTableDataSource();
  productsDisplayedColumns: string[] = Object.values<TableColumns>(TableColumns);
  productExpandedRow: ProductDTO | null;
  productsTableControl: TableControl;

  // MatPaginator Inputs
  @ViewChild('productPaginator', {static: false}) productPaginator: MatPaginator;

  constructor(private dialog: MatDialog,
              private router: Router,
              private sharedService: SharedService,
              private productService: ProductService
  ) {

    const controls = EntRequestBuilder.getTableControls();
    this.productsTableControl = new TableControl(
      controls.length,
      controls.pageSize,
      controls.pageNumber,
      controls.pageSizeOptions,
      'productId',
      'DESCENDING',
      'name',
      ''
    );
  }

  ngOnInit(): void {
    this.getListOfProducts(this.productsTableControl);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  sortData($event: Sort): void {
    if (!$event.direction) {
      return;
    }
    if (this.productPaginator != null) {
      this.productPaginator.firstPage();
    }
    this.productsTableControl.pageNumber = 0;

    if ($event.active === 'processingClass') {
      this.productsTableControl.sortField = 'processingClassByProcessingClassId.className';
    }
    else if ($event.active === 'payer') {
      this.productsTableControl.sortField = 'payerByPayerId.name';
    }
    else if ($event.active === 'providerNetwork') {
      this.productsTableControl.sortField = 'providerNetworkByProviderNetworkId.name';
    }
    else if ($event.active === 'areaOfCover') {
      this.productsTableControl.sortField = 'areaOfCoverByAreaOfCoverId.name';
    }
    else if ($event.active === 'regulator') {
      this.productsTableControl.sortField = 'regulatorByRegulatorId.name';
    }
    else {
      this.productsTableControl.sortField = $event.active;
    }
    this.productsTableControl.sortDirection = getSortDirection($event.direction);
    this.getListOfProducts(this.productsTableControl);
  }

  pageChanged($event: PageEvent): void {
    this.productsTableControl.pageNumber = $event.pageIndex;
    this.productsTableControl.pageSize = $event.pageSize;
    this.getListOfProducts(this.productsTableControl);
  }

  onValueChanged(value: string): void {
    this.productsTableControl.filterField = 'name';
    this.productsTableControl.filterValue = value;
    this.getListOfProducts(this.productsTableControl);
  }

  editEntry(event: MouseEvent, product: ProductDTO): void {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate([`enterprise-management/edit-product/${product.productId}`]);
  }

  onDeleteProductConfirmation(event: MouseEvent, product: ProductDTO): void {
    event.preventDefault();
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you wish to delete this ${product.name} product?`
    });

    dialogRef
      .afterClosed()
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.onDeleteProductConfirmed(product.productId);
        }
      });
  }

  onDeleteProductConfirmed(productId: number): void {
    this.productService.deleteProduct(productId)
      .pipe(take(1))
      .subscribe(
        (response) => {
          this.sharedService.openSnackBar(`Product with ${productId} deleted`, 'Dismiss');
          this.getListOfProducts(this.productsTableControl);
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  onDuplicateProductConfirmation(duplicateItem: boolean, product: ProductDTO): void {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you wish to duplicate this ${product.name} product?`
    });

    dialogRef
      .afterClosed()
      .subscribe((isConfirmed) => {
        if (isConfirmed && duplicateItem) {
          this.onDuplicateProductConfirmed(product.productId);
        }
      });
  }

  onDuplicateProductConfirmed(id: number): void {
    this.productService.getProductByProductId(id)
      .pipe(
        take(1),
        map((product: ProductDTO) => {
          // Modify the product properties
          product.productId = null;
          product.name += '_copy';
          product.productCode += '_copy';
          product.created = new Date();
          product.createdBy = this.sharedService.getLoggedInUserName();
          product.updated = null;
          product.updatedBy = null;
          return product;
        }),
        switchMap((copiedProduct: any) => {
          return this.productService.saveProduct(copiedProduct);
        })
      )
      .subscribe(
        (_: ProductDTO) => {
          this.getListOfProducts(this.productsTableControl);
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        });
  }

  getListOfProducts(tableControl: TableControl): void {
    const pagedRequestDTO: PagedRequestDTO = EntRequestBuilder.getListOfProductsPagedRequestDTO(tableControl);
    const pagedRequestDTOWithFieldMap: PagedRequestDTO = addFieldMapFiltersForPagedRequestDTO(pagedRequestDTO, ['name', 'productCode'], tableControl.filterValue);

    this.productService
      .getListOfProducts(pagedRequestDTOWithFieldMap)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (responseDTO: PagedResponseDTO<ProductDTO>) => {
          this.productsDataSource = new MatTableDataSource(responseDTO.resultList);
          this.productsTableControl.length = responseDTO.totalRecords;
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      });
  }
}